import React from 'react';
import { Calendar, Users } from 'lucide-react';

interface Assessment {
  ratings?: { [key: string]: number };
  rating_average?: number;
  rating_count?: number;
  notes?: string;
}

interface AssessmentSummaryProps {
  assessment: Assessment;
  evaluationDate: string;
  ageGroupName: string;
}

const AssessmentSummary: React.FC<AssessmentSummaryProps> = ({ assessment, evaluationDate, ageGroupName }) => {
  const ratings = assessment.ratings || {};
  const allScores = Object.entries(ratings).map(([key, value]) => ({
    label: key.charAt(0).toUpperCase() + key.slice(1, 4), // First letter capitalized + next 3 letters
    key,
    value
  }));

  // Use the calculated average from the database
  const average = assessment.rating_average || 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center space-x-1">
          <Calendar className="h-4 w-4" />
          <span>{evaluationDate}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Users className="h-4 w-4" />
          <span>{ageGroupName}</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex space-x-1">
          {allScores.map(({ label, value }: { label: string; value: number }) => (
            <div
              key={label}
              className={`w-12 h-8 flex items-center justify-center rounded border ${
                (value || 0) >= 4 ? 'bg-green-100 text-green-700 border-green-200' :
                (value || 0) >= 3 ? 'bg-blue-100 text-blue-700 border-blue-200' :
                (value || 0) >= 2 ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                'bg-red-100 text-red-700 border-red-200'
              }`}
            >
              <div className="text-xs font-medium">
                {label}
                <span className="ml-1">{value}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center space-x-1">
          <span className="text-sm font-medium">Avg</span>
          <span className={`text-sm font-bold ${
            average >= 4 ? 'text-green-600' :
            average >= 3 ? 'text-blue-600' :
            average >= 2 ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {average.toFixed(1)}
          </span>
        </div>
      </div>
      {assessment.notes && (
        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
          {assessment.notes}
        </p>
      )}
    </div>
  );
};

export default AssessmentSummary;